import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { authFetch, handleApiResponse, describeApiError } from '../api/authFetch';

// Manages the list of a user's chat threads (the sidebar's data source) plus
// create/rename/delete actions against the /chats API.
export const useChatThreads = ({ showMessage } = {}) => {
  const [threads, setThreads] = useState([]);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const navigate = useNavigate();

  const refreshThreads = useCallback(async () => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/chats`);
      const data = await handleApiResponse(res, navigate);
      if (data === null) return null; // redirected to signin
      setThreads(data);
      return data;
    } catch (error) {
      console.error('Error fetching chat threads:', error);
      if (showMessage) showMessage(`Failed to load chats: ${describeApiError(error)}`, "error");
      return null;
    } finally {
      setIsLoadingThreads(false);
    }
  }, [navigate, showMessage]);

  useEffect(() => {
    refreshThreads();
  }, [refreshThreads]);

  const createThread = useCallback(async (title) => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/chats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(title ? { title } : {}),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return null; // redirected to signin
      setThreads(prev => [data, ...prev]);
      return data;
    } catch (error) {
      console.error('Error creating chat thread:', error);
      if (showMessage) showMessage(`Couldn't start a new chat: ${describeApiError(error)}`, "error");
      return null;
    }
  }, [navigate, showMessage]);

  const renameThread = useCallback(async (threadId, title) => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/chats/${threadId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return null; // redirected to signin
      setThreads(prev => prev.map(t => (t.id === threadId ? data : t)));
      return data;
    } catch (error) {
      console.error('Error renaming chat thread:', error);
      if (showMessage) showMessage(`Couldn't rename that chat: ${describeApiError(error)}`, "error");
      return null;
    }
  }, [navigate, showMessage]);

  const deleteThread = useCallback(async (threadId) => {
    try {
      const res = await authFetch(`${config.API_BASE_URL}/chats/${threadId}`, {
        method: 'DELETE',
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return false; // redirected to signin
      setThreads(prev => prev.filter(t => t.id !== threadId));
      return true;
    } catch (error) {
      console.error('Error deleting chat thread:', error);
      if (showMessage) showMessage(`Couldn't delete that chat: ${describeApiError(error)}`, "error");
      return false;
    }
  }, [navigate, showMessage]);

  return {
    threads,
    isLoadingThreads,
    refreshThreads,
    createThread,
    renameThread,
    deleteThread,
  };
};
